import React, { useState, useRef, useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  IconChevronLeft,
  IconChevronRight,
  IconInfoCircle,
  IconInfoCircleFilled,
  IconSparkles,
  IconTools,
  IconArrowLeft,
  IconDotsVertical,
} from "@tabler/icons-react";
import useStartupStore from "../../stores/startupStore";
import { Alert, Button, Tooltip, Menu } from "@mantine/core";
import MobileBottomDrawer from "./MobileBottomDrawer";
import {
  HtmlEditor,
  Image,
  Inject,
  QuickToolbar,
  RichTextEditorComponent,
  PasteCleanup,
  Table,
  Toolbar,
  Link,
} from "@syncfusion/ej2-react-richtexteditor";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import moment from "moment";
import useUserStore from "../../stores/userStore";
import OutcomeValidation from "../../stories/OutcomeValidation";
import { AssignMembers } from "./TextEditor";
import RevisionHistoryViewer from "../../stories/RevisionHistoryViewer";
import { CompactSaveStatus, useAutoSave } from "../AutoSave";
import { IconToolsOff } from "@tabler/icons-react";
import SuggestedTasks from "../../stories/SuggestedTasks";

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  border-bottom: 1px solid #e5e7eb;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px 6px;
`;

const NavIconButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c45e7;
  border-radius: 6px;
  transition: background 0.2s;
  min-width: 32px;
  min-height: 32px;

  &:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    background: #f3f4f6;
  }
`;

const CategoryName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  flex: 1;
  text-align: center;
  padding: 0 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubcategoryTabs = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 16px 12px;
  background: white;
  overflow-x: auto;
  border-top: 1px solid #f1f3f5;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SubcategoryTab = styled.button`
  padding: 6px 12px;
  border: none;
  background: ${(props) => (props.active ? "#6c45e7" : "#f3f4f6")};
  color: ${(props) => (props.active ? "white" : "#4b5563")};
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;

  &:active {
    transform: scale(0.96);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding-block: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
`;

const OutcomeAlert = styled(Alert)`
  .mantine-Alert-message {
    font-size: 12px;
    line-height: 1.5;
  }
`;

const EditorWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 400px;

  .e-richtexteditor {
    border: none;
  }

  .e-toolbar {
    border: none;
    border-bottom: 1px solid #e5e7eb;
    padding: 8px 4px;
  }

  .e-content {
    padding: 12px;
  }
`;

const FloatingActionButton = styled.button`
  position: fixed;
  bottom: 48px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #6c45e7;
  border: none;
  box-shadow: 0 4px 12px rgba(108, 69, 231, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  transition: all 0.2s;

  &:active {
    transform: scale(0.95);
  }

  svg {
    color: white;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const StartupImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  cursor: pointer;
`;

const ActionButton = styled(Button)`
  .mantine-Button-inner{
    font-size: 12px;
    padding: 0px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #6c45e7;
  font-size: 12px;
  font-weight: 600;
  padding: 6px;
  cursor: pointer;
  border-radius: 6px;

  &:active {
    opacity: 0.7;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;



const MobileCategoryDetailView = ({ onBack, onViewTaskBoard }) => {
  const { t } = useTranslation();
  const [showOutcome, setShowOutcome] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showToolbar, setShowToolbar] = useState(true);

  const rteInstance = useRef(null);
  const blobUrlMap = useRef(new Map());
  const uploadedImageUrls = useRef([]);

  const {
    program,
    selectedProgram,
    setSelectedProgram,
    content,
    setContent,
    isOwner,
    isMember,
    isSubmitted,
    slug,
    submitPendingChanges,
  } = useStartupStore((state) => ({
    program: state.program,
    selectedProgram: state.selectedProgram,
    setSelectedProgram: state.setSelectedProgram,
    content: state.content,
    setContent: state.setContent,
    isOwner: state.isOwner,
    isMember: state.isMember,
    isSubmitted: state.isSubmitted,
    slug: state.slug,
    submitPendingChanges: state.submitPendingChanges
  }));

  const {
    user: { enable_ai: ai },
  } = useUserStore((state) => state.user);


  const currentCategory = program?.categories?.[selectedProgram.cid];
  const currentSubcategory =
    currentCategory?.sub_categories?.[selectedProgram.sid];
  const subcategoryContent =
    content?.[selectedProgram.cid]?.[selectedProgram.sid];

  const readOnly = !(isOwner || isMember) || isSubmitted;
  const pageId = selectedProgram.cid;
  const index = selectedProgram.sid;
    // Get subcategory ID for auto-save
  const subcategoryId = content[pageId]?.[index]?.id;

  const handlePrevCategory = () => {
    if (selectedProgram.cid > 0) {
      setSelectedProgram({
        cid: selectedProgram.cid - 1,
        sid: 0,
        switch: true,
      });
    }
  };

  const handleNextCategory = () => {
    if (selectedProgram.cid < program.categories.length - 1) {
      setSelectedProgram({
        cid: selectedProgram.cid + 1,
        sid: 0,
        switch: true,
      });
    }
  };

  const handleSubcategoryChange = (subIndex) => {
    setSelectedProgram({
      cid: selectedProgram.cid,
      sid: subIndex,
      switch: true,
    });
  };

  const saveEditorContent = useCallback(() => {
    if (!rteInstance.current) return;
    try {
      let htmlContent = rteInstance.current.getHtml();

      if (htmlContent.includes("blob:") && blobUrlMap.current.size > 0) {
        blobUrlMap.current.forEach((permanentUrl, blobUrl) => {
          htmlContent = htmlContent.replace(blobUrl, permanentUrl);
        });
      }

      setContent(htmlContent);
    } catch (error) {
      console.error("Failed to save editor content:", error);
    }
  }, [setContent]);

  const debouncedSave = useCallback(debounce(saveEditorContent, 300), [
    saveEditorContent,
  ]);
  const autoSave = useAutoSave(subcategoryId, {
    enabled: !!(isOwner || isMember) && !isSubmitted,
    debounceDelay: 1200,
    changeThreshold: 10,
    onSaveSuccess: () => {},
    onSaveError: () => {}
  });

  const handleEditorChange = useCallback(() => {
    debouncedSave();
    if (rteInstance.current && subcategoryId && autoSave) {
      const htmlContent = rteInstance.current.getHtml();
      autoSave.saveContent(htmlContent);
    }
  }, [debouncedSave, subcategoryId, autoSave]);

  const insertImageSettings = useMemo(
    () => ({
      allowedTypes: [".jpeg", ".jpg", ".png", ".gif", ".svg"],
      display: "inline",
      saveUrl: "/rich_text_images/upload",
      removeUrl: "/rich_text_images/delete",
    }),
    []
  );

  const handleCommit = async() => {
    // Handle commit logic
    setDrawerOpen(false);
    try {
      const result = await submitPendingChanges();
    } catch (error) {
      console.log("error in commmiting changes ", error);
    }
  };

  const handleViewTaskboard = () => {
    // Handle taskboard view
    onViewTaskBoard();
    setDrawerOpen(false);
  };





  if (!currentCategory) {
    return (
      <>
        <BackButton onClick={onBack}>
          <IconArrowLeft size={12} />
          Back to Roadmap
        </BackButton>
        <div>Loading...</div>
      </>
    );
  }


  return (
    <>
      <StickyHeader>
        <HeaderRow>
          <BackButton onClick={onBack} aria-label="Back to Roadmap">
            <IconArrowLeft size={14} />
            Back
          </BackButton>
          <NavIconButton
            onClick={handlePrevCategory}
            disabled={pageId === 0}
            aria-label="Previous category"
          >
            <IconChevronLeft size={18} />
          </NavIconButton>
          <CategoryName>{currentCategory.name}</CategoryName>
          <NavIconButton
            onClick={handleNextCategory}
            disabled={pageId === program.categories.length - 1}
            aria-label="Next category"
          >
            <IconChevronRight size={18} />
          </NavIconButton>
        </HeaderRow>
        <SubcategoryTabs>
          {currentCategory.sub_categories?.map((sub, index) => (
            <SubcategoryTab
              key={sub.id}
              active={selectedProgram.sid === index}
              onClick={() => handleSubcategoryChange(index)}
            >
              {sub.name}
            </SubcategoryTab>
          ))}
        </SubcategoryTabs>
      </StickyHeader>

        <ContentArea>
          {showOutcome && subcategoryContent?.outcome && (
            <OutcomeAlert
              variant="light"
              color="blue"
              icon={<IconInfoCircle />}
              withCloseButton
              onClose={() => setShowOutcome(false)}
            >
              {subcategoryContent.outcome}
            </OutcomeAlert>
          )}
          <ActionsRow>
            {!isSubmitted && <AssignMembers key={`${pageId}-${index}`} />}
            {subcategoryId &&
              (isOwner || isMember) &&
              !isSubmitted &&
              autoSave.enabled && (
                <CompactSaveStatus subcategoryId={subcategoryId} />
              )}
            {(isOwner || isMember) && !isSubmitted && (
              <Menu shadow="md" width={200} position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconDotsVertical size={14} />}
                  >
                    More
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<IconInfoCircle size={14} />}
                    onClick={() => setShowOutcome((v) => !v)}
                  >
                    {showOutcome ? "Hide outcome" : "Show outcome"}
                  </Menu.Item>
                  <Menu.Item
                    icon={showToolbar ? <IconToolsOff size={14} /> : <IconTools size={14} />}
                    onClick={() =>
                      setShowToolbar((p) => {
                        const newState = !p;
                        Mixpanel.button(
                          newState ? "Show Toolbar" : "Hide Toolbar",
                          {
                            location: "Content Editor",
                            startup_slug: slug,
                          }
                        );
                        return newState;
                      })
                    }
                  >
                    {showToolbar ? "Hide toolbar" : "Show toolbar"}
                  </Menu.Item>
                  {ai && <Menu.Item><OutcomeValidation /></Menu.Item>}
                  {(isOwner || isMember) &&
                    content?.[pageId]?.[index]?.id && (
                      <Menu.Item>
                        <RevisionHistoryViewer
                          startupEntitySlug={slug}
                          subCategoryId={content[pageId][index].id}
                        />
                      </Menu.Item>
                    )}
                </Menu.Dropdown>
              </Menu>
            )}
          </ActionsRow>
          <EditorWrapper>
            <RichTextEditorComponent
              ref={rteInstance}
              key={`${pageId}-${index}`}
              value={subcategoryContent?.response || ""}
              placeholder={t("startups_page.details") || "Your text here..."}
              readonly={readOnly}
              toolbarSettings={useMemo(
                () => ({
                  enable: showToolbar,
                  type: "Scrollable",
                  items: [
                    "Bold",
                    "Italic",
                    "|",
                    "UnorderedList",
                    "|",
                    "Image",
                    "|",
                    "Undo",
                    "Redo",
                  ],
                }),
                [showToolbar]
              )}
              insertImageSettings={insertImageSettings}
              pasteCleanupSettings={{
                allowedStyleProps: ["color", "margin", "font-size"],
                deniedAttrs: ["class", "title", "id"],
                deniedTags: ["a"],
                keepFormat: false,
                plainText: false,
                prompt: true,
              }}
              beforeImageUpload={(args) => {
                if (args && args.currentRequest) {
                  const metaTag = document.querySelector(
                    "meta[name='csrf-token']"
                  );
                  const token = metaTag ? metaTag.content : "";
                  args.currentRequest.setRequestHeader("X-CSRF-Token", token);
                }
                return true;
              }}
              imageUploadSuccess={(args) => {
                try {
                  let responseData;
                  if (typeof args.e.target.response === "string") {
                    responseData = JSON.parse(args.e.target.response);
                  } else {
                    responseData = args.e.target.response;
                  }
                  if (!responseData || !responseData.url) return;
                  const permanentUrl = responseData.url;
                  uploadedImageUrls.current.push(permanentUrl);
                } catch (error) {
                  console.error("Error handling image upload success:", error);
                }
              }}
              imageUploadFailed={(args) => {
                console.error("Image upload failed:", args);
              }}
              change={handleEditorChange}
              height="100%"
              saveInterval={200}
            >
              <Inject
                services={[
                  Toolbar,
                  HtmlEditor,
                  Image,
                  Link,
                  Table,
                  QuickToolbar,
                  PasteCleanup,
                ]}
              />
            </RichTextEditorComponent>
          </EditorWrapper>

          {(isOwner || isMember) && (
            <ButtonGroup>
              <ActionButton
                color="violet"
                variant="outline"
                fullWidth
                onClick={handleCommit}
              >
                Commit changes
              </ActionButton>
              <ActionButton
                color="violet"
                fullWidth
                onClick={handleViewTaskboard}
              >
                View Taskboard
              </ActionButton>
            </ButtonGroup>
          )}
          {(isOwner || isMember) && !isSubmitted && (
            <SuggestedTasks
              subcategoryId={subcategoryId}
              readOnly={readOnly}
              setShowTask={onViewTaskBoard}
            />
          )}
        </ContentArea>

        {(isOwner || isMember) && (
          <FloatingActionButton onClick={() => setDrawerOpen(true)}>
            <IconSparkles size={24} />
          </FloatingActionButton>
        )}

      <MobileBottomDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCommit={handleCommit}
        onViewTaskboard={onViewTaskBoard}
      />
    </>
  );
};

export default MobileCategoryDetailView;
